import { request } from './client'

/* W-120 and W-121 — everyone who can sign in, and the two who cannot yet.

   THE SEPARATION THE SCREENS ARE BUILT ON: they own identity, we own authorisation. A
   person's name and employee number come from the property system; their ROLE is ours,
   and nothing about permissions crosses that interface.

   **AND IN THIS PHASE THERE IS NO "THEY"**, so `addPerson` exists and every row records
   whether it arrived that way. Change Note 05. */

export type UserStanding = 'ACTIVE' | 'NO_ROLE' | 'DISABLED'

export type UserSource = 'SYNCED' | 'ENTERED_HERE'

export interface UserRoleView {
  propertyId: string
  propertyName: string
  propertyNameAr: string | null
  roleId: string
  roleCode: string
  roleName: string
  roleNameAr: string | null
}

export interface UserRow {
  userId: string
  employeeId: string
  displayName: string
  displayNameAr: string | null
  standing: UserStanding
  source: UserSource
  preferredLanguage: string
  roles: UserRoleView[]
  /** NULL means never signed in — a different fact from "a long time ago", and the
   *  screen says which in words rather than with a dash. */
  lastSeenAtUtc: string | null
  liveSessions: number
}

export interface UserBoard {
  users: UserRow[]
  /** Null in this phase: there is no property system, so "last synchronised" has no
   *  honest value and "never" would read as a sync that is broken. */
  lastSyncedAtUtc: string | null
}

export interface SessionView {
  sessionId: string
  /** A CODE — ANDROID, CHROME, HANDSET_APP — not a phrase, so the words can be drawn
   *  in the reader's own language. NULL when the User-Agent was not recognisable at
   *  all, which renders as "Unknown device" and NEVER as a guess: this is the row
   *  somebody reads before cutting a handset off. */
  deviceLabel: string | null
  userAgent: string | null
  issuedAtUtc: string
  expiresAtUtc: string
  lastSeenAtUtc: string
  endedAtUtc: string | null
  endedReason: string | null
  /** Is this the session the person looking at the screen is holding? */
  isYours: boolean
}

export interface UserActivityView {
  tasksCompleted: number
  inspectionsFailed: number
  requestsFulfilled: number
  fromLocalDate: string
  toLocalDate: string
}

export interface RoleOption {
  roleId: string; code: string; name: string; nameAr: string | null
}
export interface PropertyOption { propertyId: string; name: string; nameAr: string | null }

export interface UserDetailView {
  person: UserRow
  email: string | null
  status: string
  lastSyncedAtUtc: string | null
  deactivatedAtUtc: string | null
  deactivatedReason: string | null
  sessions: SessionView[]
  activity: UserActivityView
  roles: RoleOption[]
  properties: PropertyOption[]
}

export function fetchUsers(
  options: { signal?: AbortSignal } = {},
): Promise<UserBoard> {
  return request<UserBoard>('/users', options.signal ? { signal: options.signal } : {})
}

export function fetchUser(
  userId: string, options: { signal?: AbortSignal } = {},
): Promise<UserDetailView> {
  return request<UserDetailView>(
    `/users/${userId}`, options.signal ? { signal: options.signal } : {})
}

export function addPerson(body: {
  employeeId: string
  displayName: string
  displayNameAr?: string | null
  preferredLanguage?: string
}): Promise<{ userId: string }> {
  return request('/users', { method: 'POST', body })
}

export function assignUserRole(
  userId: string, body: { propertyId: string; roleId: string },
): Promise<UserDetailView> {
  return request(`/users/${userId}/role`, { method: 'PUT', body })
}

export function removeUserRole(
  userId: string, propertyId: string,
): Promise<UserDetailView> {
  return request(`/users/${userId}/role/${propertyId}`, { method: 'DELETE' })
}

export function setUserPreferences(
  userId: string, body: { displayNameAr: string | null; preferredLanguage: string },
): Promise<UserDetailView> {
  return request(`/users/${userId}/preferences`, { method: 'PUT', body })
}

/** Disabling ends every session on the spot — the leaver is refused on their next tap,
 *  not at the next expiry. */
export function setUserEnabled(
  userId: string, body: { enabled: boolean; reason: string },
): Promise<UserDetailView> {
  return request(`/users/${userId}/enabled`, { method: 'PUT', body })
}

export function endSession(sessionId: string): Promise<UserDetailView> {
  return request(`/sessions/${sessionId}`, { method: 'DELETE' })
}

export function endAllSessions(userId: string): Promise<UserDetailView> {
  return request(`/users/${userId}/sessions`, { method: 'DELETE' })
}
